/*
Permission keys identify permission behavior assigned to the microservices in securitySchema.
Currently they match permission names, but in a future, we might add more unique keys like
"billing-scheduling", "billing-get", "billing-post", etc.
*/
var PERMISSION_KEYS = Object.freeze({
  patients: 'patients',
  dashboard: 'dashboard',
  scheduling: 'scheduling',
  charting: 'charting',
  billing: 'billing',
  settings: 'settings',
  chartingOrScheduling: 'chartingOrScheduling',
  chartingOrBilling: 'chartingOrBilling',
  "public": 'public',
  patientUser: 'patientUser',
  superUser: 'superUser',
  support: 'support'
});

/**
 * Similar to PERMISSION_KEYS, but the include information about the auth
 * type, or special keywords like PUBLIC.
 * Usage examples:
 * securitySchema: { ...SECURITY_SCHEMA_KEYS.PUBLIC }
 * securitySchema: { ...SECURITY_SCHEMA_KEYS.PRACTICE.patients }
 * This will allow for adding auth type to keys, and combine with other auth types.
 */
var SECURITY_SCHEMA_KEYS = Object.freeze({
  PUBLIC: {
    PUBLIC: PERMISSION_KEYS["public"]
  },
  PATIENT: {
    PATIENT_COGNITO: PERMISSION_KEYS.patientUser
  },
  PRACTICE: {
    patients: {
      PRACTICE_COGNITO: PERMISSION_KEYS.patients
    },
    dashboard: {
      PRACTICE_COGNITO: PERMISSION_KEYS.dashboard
    },
    scheduling: {
      PRACTICE_COGNITO: PERMISSION_KEYS.scheduling
    },
    charting: {
      PRACTICE_COGNITO: PERMISSION_KEYS.charting
    },
    billing: {
      PRACTICE_COGNITO: PERMISSION_KEYS.billing
    },
    settings: {
      PRACTICE_COGNITO: PERMISSION_KEYS.settings
    },
    chartingOrScheduling: {
      PRACTICE_COGNITO: PERMISSION_KEYS.chartingOrScheduling
    },
    chartingOrBilling: {
      PRACTICE_COGNITO: PERMISSION_KEYS.chartingOrBilling
    },
    superUser: {
      PRACTICE_COGNITO: PERMISSION_KEYS.superUser
    }
  },
  MICROSERVICE: {
    MICROSERVICE: 'microservice'
  },
  SALESFORCE: {
    SALESFORCE: 'salesforce'
  },
  SUPPORT: {
    SUPPORT_COGNITO: PERMISSION_KEYS.support
  }
});

/*
Permission names identify microservices and UI permission groups.
*/
var PERMISSION_NAMES = Object.freeze({
  patients: 'patients',
  dashboard: 'dashboard',
  scheduling: 'scheduling',
  charting: 'charting',
  billing: 'billing',
  settings: 'settings',
  support: 'support'
});

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

var _PERMISSION_KEY_SPECI;
var PERMISSIONS_V_1 = Object.freeze([{
  name: PERMISSION_NAMES.patients,
  access: true
}, {
  name: PERMISSION_NAMES.dashboard,
  access: true
}, {
  name: PERMISSION_NAMES.scheduling,
  access: true
}, {
  name: PERMISSION_NAMES.charting,
  access: true
}, {
  name: PERMISSION_NAMES.billing,
  access: true
}, {
  name: PERMISSION_NAMES.settings,
  access: true
}]);
var PERMISSIONS_V_1_COUNT = 7;
var PERMISSION_SCHEMA_ERROR = 'The permission data schema is not in supported. The permission object must have .v (version) and .a (access) properties.';
var getDefaultPermissions = function getDefaultPermissions() {
  return PERMISSIONS_V_1;
};
var PERMISSIONS_ACCESS_ALL = getDefaultPermissions().map(function (x) {
  return _objectSpread2(_objectSpread2({}, x), {}, {
    access: true
  });
});
var PERMISSIONS_ACCESS_NONE = getDefaultPermissions().map(function (x) {
  return _objectSpread2(_objectSpread2({}, x), {}, {
    access: false
  });
});
var withPermissions = function withPermissions() {
  for (var _len = arguments.length, permissionNames = new Array(_len), _key = 0; _key < _len; _key++) {
    permissionNames[_key] = arguments[_key];
  }
  return PERMISSIONS_ACCESS_NONE.reduce(function (arr, item) {
    arr.push(permissionNames.includes(item.name) ? _objectSpread2(_objectSpread2({}, item), {}, {
      access: true
    }) : item);
    return arr;
  }, []);
};
var withAllPermissionsExcept = function withAllPermissionsExcept() {
  for (var _len2 = arguments.length, permissionNames = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    permissionNames[_key2] = arguments[_key2];
  }
  return PERMISSIONS_ACCESS_ALL.reduce(function (arr, item) {
    arr.push(permissionNames.includes(item.name) ? _objectSpread2(_objectSpread2({}, item), {}, {
      access: false
    }) : item);
    return arr;
  }, []);
};
var getDefaultPermissionNames = function getDefaultPermissionNames() {
  return getDefaultPermissions().map(function (x) {
    return x.name;
  });
};
var convertPermissionsToBinary = function convertPermissionsToBinary(permissions) {
  var binaryString = getDefaultPermissions().reduce(function (result, item) {
    return permissions && permissions.some(function (x) {
      return x.name === item.name && x.access;
    }) ? "".concat(result, "1") : "".concat(result, "0");
  }, '');
  return binaryString;
};
var convertUserPermissionsToBinary = function convertUserPermissionsToBinary(userPermissions) {
  var superUser = userPermissions.superUser ? '1' : '0';
  var permissions = convertPermissionsToBinary(userPermissions.permissions);
  var binaryString = "".concat(superUser).concat(permissions);
  return binaryString;
};
var encodeUserPermissionsToHex = function encodeUserPermissionsToHex(userPermissions) {
  var version = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '1';
  if (version === '1') {
    return parseInt(convertUserPermissionsToBinary(userPermissions), 2).toString(16).toUpperCase();
  }
  throw new Error("Permission version ".concat(version, " is not supported"));
};
var encodeUserPermissions = function encodeUserPermissions(userPermissions) {
  var version = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '1';
  return {
    v: version,
    a: encodeUserPermissionsToHex(userPermissions, version)
  };
};
var validateCognitoUserPermissionsSchema = function validateCognitoUserPermissionsSchema(model) {
  if (!model || !model.v || !model.a) {
    throw Error(PERMISSION_SCHEMA_ERROR);
  }
  if (model.v !== '1') {
    throw new Error("Permission version ".concat(model.v, " is not supported"));
  }
};
var decodeUserPermissions = function decodeUserPermissions(permissionData) {
  validateCognitoUserPermissionsSchema(permissionData);
  var permissionsBinary = parseInt(permissionData.a, 16).toString(2).padStart(PERMISSIONS_V_1_COUNT, '0');
  var userPermissions = {
    superUser: permissionsBinary.charAt(0) === '1',
    permissions: PERMISSIONS_V_1.map(function (x, index) {
      return _objectSpread2(_objectSpread2({}, x), {}, {
        access: permissionsBinary.charAt(index + 1) === '1'
      });
    })
  };
  return userPermissions;
};
var singlePermissionHandler = function singlePermissionHandler(permissions, permissionKey) {
  return permissions.filter(function (x) {
    return x.access;
  }).map(function (x) {
    return x.name;
  }).includes(permissionKey);
};
var multiPermissionOrHandler = function multiPermissionOrHandler(permissions, permissionKeyArray) {
  var validPermissions = permissions.filter(function (x) {
    return x.access;
  }).map(function (x) {
    return x.name;
  });
  return validPermissions.some(function (x) {
    return permissionKeyArray.includes(x);
  });
};
var PERMISSION_KEY_SPECIAL_HANDLERS = (_PERMISSION_KEY_SPECI = {}, _defineProperty(_PERMISSION_KEY_SPECI, PERMISSION_KEYS.chartingOrScheduling, function (permissions) {
  return multiPermissionOrHandler(permissions, [PERMISSION_KEYS.charting, PERMISSION_KEYS.scheduling]);
}), _defineProperty(_PERMISSION_KEY_SPECI, PERMISSION_KEYS.chartingOrBilling, function (permissions) {
  return multiPermissionOrHandler(permissions, [PERMISSION_KEYS.charting, PERMISSION_KEYS.billing]);
}), _PERMISSION_KEY_SPECI);
var validateAccessByPermissionKey = function validateAccessByPermissionKey(permissionKey, tenantPermissionText) {
  var userPermissions = decodeUserPermissions(tenantPermissionText);
  if (userPermissions.superUser) return true;
  var handler = PERMISSION_KEY_SPECIAL_HANDLERS[permissionKey] || singlePermissionHandler;
  var result = handler.call(undefined, userPermissions.permissions, permissionKey);
  return result;
};

var debugLog = function debugLog(securitySchema, name, value) {
  /* istanbul ignore if */
  if (securitySchema.debug) {
    // eslint-disable-next-line no-console
    console.log("".concat(name, " :>> "), value);
  }
};
var isPublicSecuritySchema = function isPublicSecuritySchema(securitySchema) {
  return securitySchema.PUBLIC !== undefined;
};
var isMicroserviceAuth = function isMicroserviceAuth(customAccess) {
  return customAccess.MICROSERVICE !== undefined;
};
var isSalesforceAuth = function isSalesforceAuth(customAccess) {
  return customAccess.SALESFORCE !== undefined;
};
var isPracticeAuth = function isPracticeAuth(customAccess) {
  return customAccess.PRACTICE_COGNITO !== undefined;
};
var isPatientAuth = function isPatientAuth(customAccess) {
  return customAccess.PATIENT_COGNITO !== undefined;
};
var isSupportAuth = function isSupportAuth(securitySchema) {
  return securitySchema.SUPPORT_COGNITO !== undefined;
};
var isPatientUserAllowed = function isPatientUserAllowed(securitySchema, customAccess) {
  return securitySchema.PATIENT_COGNITO === PERMISSION_KEYS.patientUser && (isPatientAuth(customAccess) || isPracticeAuth(customAccess));
};
var isSalesforceAuthAllowed = function isSalesforceAuthAllowed(securitySchema, customAccess) {
  return isSalesforceAuth(customAccess) && securitySchema.SALESFORCE !== undefined;
};
var isSupportUserAllowed = function isSupportUserAllowed(securitySchema, customAccess) {
  return isSupportAuth(securitySchema) && customAccess.SUPPORT_COGNITO !== undefined;
};
function checkAccessGranted(securitySchema, customAccess, tenantId) {
  var permissionTextForTenant = JSON.parse(customAccess)[tenantId].p;
  return validateAccessByPermissionKey(securitySchema, permissionTextForTenant);
}
var validateSecuritySchema = (function (securitySchema, customAccess, tenantId) {
  debugLog(securitySchema, 'securitySchema', securitySchema);
  debugLog(securitySchema, 'customAccess', customAccess);
  if (isPublicSecuritySchema(securitySchema)) return true;
  if (isMicroserviceAuth(customAccess)) return true;
  if (isSalesforceAuthAllowed(securitySchema, customAccess)) return true;
  if (isPatientUserAllowed(securitySchema, customAccess)) return true;
  if (isSupportUserAllowed(securitySchema, customAccess)) return true;
  if (!isPracticeAuth(customAccess)) return false;
  if (!tenantId) return false;
  return checkAccessGranted(securitySchema.PRACTICE_COGNITO, customAccess.PRACTICE_COGNITO, tenantId);
});

var COGNITO_CUSTOM_FIELD_MAX_LENGTH = 2048;
var COGNITO_CUSTOM_FIELD_MAX_COUNT = 10;
var COGNITO_CUSTOM_ACCESS_NAME = 'custom:access';
var EXCEEDED_COGNITO_CUSTOM_FIELDS_LIMIT_ERROR = 'Too many tenants stored in custom fields. Data exceeded max length limit';
var DELIMITER = '^#@';
var textToCustomAttributes = function textToCustomAttributes(text) {
  var maxLength = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : COGNITO_CUSTOM_FIELD_MAX_LENGTH;
  var attributesCount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : COGNITO_CUSTOM_FIELD_MAX_COUNT;
  if (text.length > maxLength * attributesCount) {
    throw Error("".concat(EXCEEDED_COGNITO_CUSTOM_FIELDS_LIMIT_ERROR, ": ").concat(text.length, " > ").concat(maxLength, " * ").concat(attributesCount));
  }
  var attributes = {};
  var items = text.split('').reduce(function (acc, item, index) {
    return index % maxLength || !index ? acc.concat(item) : acc.concat(DELIMITER, item);
  }, '').split(DELIMITER);
  for (var i = 0; i < attributesCount; i += 1) {
    attributes["".concat(COGNITO_CUSTOM_ACCESS_NAME).concat(i || '')] = i <= items.length - 1 ? items[i] : '';
  }
  return attributes;
};
var getCustomAccessTextFromAttributes = function getCustomAccessTextFromAttributes(payload) {
  var customAccess = '';
  for (var i = 0; i < COGNITO_CUSTOM_FIELD_MAX_COUNT; i += 1) {
    var name = "".concat(COGNITO_CUSTOM_ACCESS_NAME).concat(i || '');
    customAccess = "".concat(customAccess).concat(payload[name] ? payload[name] : '');
  }
  return customAccess;
};

export { COGNITO_CUSTOM_ACCESS_NAME, PERMISSION_KEYS, PERMISSION_NAMES, SECURITY_SCHEMA_KEYS, decodeUserPermissions, encodeUserPermissions, getCustomAccessTextFromAttributes, getDefaultPermissionNames, getDefaultPermissions, textToCustomAttributes, validateAccessByPermissionKey, validateSecuritySchema, withAllPermissionsExcept, withPermissions };
